import {Box, Card, CardContent, Divider, Typography, Chip, List, ListItem, ListItemText} from '@mui/material';
import PatientSettings from '../PatientSettings/PatientSettings';
import {memo, useContext, useEffect, useState} from 'react';
import {Context} from '../..';
import SendMessage from '../SendMessage/SendMessage';
import useFormattedDate from '../../hooks/useFormattedDate';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import {getAllBalances} from '../../http/userAPI';

function PatientCard({patient, handleClickGetPatientInfo}) {
    const {clinic} = useContext(Context);
    const [balances, setBalances] = useState(null);
    const [patientPoints, setPatientPoints] = useState(patient?.points || 0);

    const birthday = useFormattedDate(patient.birthday);

    useEffect(() => {
        setPatientPoints(patient?.points || 0);
    }, [patient?.points]);

    useEffect(() => {
        if (patient?.id && patient?.isParent) {
            getAllBalances(patient.id)
                .then(data => setBalances(data))
                .catch(() => setBalances(null));
        } else {
            setBalances(null);
        }
    }, [patient?.id, patient?.isParent]);

    return (
        <Box sx={{width: '100%', maxWidth: 500}}>
            <Card sx={{
                boxShadow: 3, 
                borderRadius: 2,
                border: patient.isChild ? '2px solid #4caf50' : 'none'
            }}>
                <CardContent>
                    {/* Заголовок карточки */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography
                            variant='h5'
                            component='div'
                            sx={{fontWeight: 'bold'}}
                        >
                            Информация о пациенте
                        </Typography>
                        {patient.isChild && (
                            <Chip 
                                icon={<ChildCareIcon />}
                                label="Ребенок" 
                                color="success" 
                                size="small"
                            />
                        )}
                    </Box>

                    <Divider sx={{mb: 2}}/>

                    {/* Основная информация о пациенте */}
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        <Typography variant='body1'>
                            <strong>Права администратора:</strong>{' '}
                            {patient.isAdmin ? 'Выданы' : 'Не выданы'}
                        </Typography>
                        {!!patient?.cardId && (
                            <Typography variant='body1'>
                                <strong>Номер карты пациента:</strong> {patient.cardId}
                            </Typography>
                        )}
                        {patient.patientClinicId && (
                            <Typography variant='body1'>
                                <strong>ID пациента:</strong> {patient.patientClinicId}
                            </Typography>
                        )}
                        <Typography variant='body1'>
                            <strong>Имя:</strong> {patient.name}
                        </Typography>
                        <Typography variant='body1'>
                            <strong>Телефон:</strong> {patient.phone}
                        </Typography>
                        <Typography variant='body1'>
                            <strong>Бот заблокирован:</strong> {patient.isBotBlocked ? 'Да' : 'Нет'}
                        </Typography>
                        {
                            !!patient?.birthday && (
                                <Typography variant='body1'>
                                    <strong>Дата рождения:</strong> {birthday}
                                </Typography>
                            )
                        }
                        <Typography variant='body1' sx={{color: 'primary.main'}}>
                            <strong>Баланс:</strong> {patientPoints} баллов{' '}
                        </Typography>
                        
                        {balances && balances.children && balances.children.length > 0 && (
                            <>
                                <Divider sx={{my: 1.5}}/>
                                <Typography variant='subtitle2' sx={{fontWeight: 'bold', mb: 0.5}}>
                                    Балансы детей:
                                </Typography>
                                <List dense disablePadding>
                                    {balances.children.map((child) => (
                                        <ListItem key={child.id} sx={{px: 0, py: 0.25}}>
                                            <ListItemText
                                                primary={`${child.name || 'Без имени'}: ${child.balance} баллов`}
                                                primaryTypographyProps={{variant: 'body2'}}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                    </Box>

                    <Divider sx={{my: 2}}/>

                    {/* Компонент настроек пациента */}
                    <SendMessage patient={patient}/>
                    <PatientSettings
                        clinic={clinic}
                        patient={patient}
                        handleClickGetPatientInfo={handleClickGetPatientInfo}
                        setPatientPoints={setPatientPoints}
                    />
                </CardContent>
            </Card>
        </Box>
    );
}

export default memo(PatientCard);
